import React from 'react'
import { isModalLink } from '@moonpig/web-core-link'
import { ModuleErrorBoundary } from '@moonpig/web-shared-products'
import { HomepageBannerVariantModule } from './HomepageBannerVariant'
import { ProductListModule } from './ProductList'
import { HomepageCTABannerVariantModule } from './HomepageCTABannerVariant'
import { RemindersCTAStripModule } from './RemindersCTAStrip'
import { RecommendationsForCustomerModule } from './RecommendationsForCustomer'
import { PlaceholderModule } from './Placeholder'
import { VideoModule } from './Video'
import { TwoColumnPlacardModule } from './TwoColumnPlacard'
import { DeliveryStripModule } from './DeliveryStrip'
import { CTAStripModule } from './CTAStrip'
import { OfferStripModule } from './OfferStrip'
import { ModuleSection } from '../components/ModuleContainer'
import { ContentModule } from '../types'
import {
  MODULE_BREADCRUMBS,
  MODULE_CARD,
  MODULE_CAROUSEL,
  MODULE_CTA_STRIP,
  MODULE_DELIVERY_STRIP,
  MODULE_EMAIL_CAPTURE,
  MODULE_HEADING,
  MODULE_HERO,
  MODULE_INFO,
  MODULE_MISSION_BUTTONS,
  MODULE_OFFER_STRIP,
  MODULE_PLACARDS,
  MODULE_PLACEHOLDER,
  MODULE_RECOMMENDATIONS_FOR_CUSTOMER,
  MODULE_SEARCH_RESULTS,
  MODULE_SEO,
  MODULE_TABBED_PRODUCT_LISTS,
  MODULE_TILES,
  MODULE_TWO_COLUMN_PLACARD,
  MODULE_USP_CAROUSEL,
  MODULE_VIDEO,
} from './constants'
import { mapModule } from './map-modules'
import { ModuleContext } from './types'

type TrackingData = {
  moduleIndex: number
  moduleType: string
  productCarouselIndexOnPage: number
  numberOfProductCarouselsOnPage: number
}

type ProductListOptions = {
  alternativeButton?: boolean
  excludeRudeCards?: boolean
}

type RenderPageModuleParams = {
  module: ContentModule
  context: ModuleContext
  backgroundColor: string
  page: 'home' | 'landing'
  trackingData: TrackingData
  ctaStripVariant: 'homepage' | 'standard'
  deliveryStripVariant: 'homepage' | 'standard'
  productListOptions?: ProductListOptions
}

const LARGE_SPACING_PADDING = {
  py: { xs: 8, lg: 10 },
}

const renderCtaStripModule = ({
  module,
  trackingData,
  moduleTrackingData,
  ctaStripVariant,
}: {
  module: Extract<ContentModule, { type: typeof MODULE_CTA_STRIP }>
  trackingData: TrackingData
  moduleTrackingData: Pick<TrackingData, 'moduleIndex' | 'moduleType'>
  ctaStripVariant: RenderPageModuleParams['ctaStripVariant']
}) => {
  if (isModalLink(module.cta.url)) {
    return (
      <RemindersCTAStripModule trackingData={moduleTrackingData} {...module} />
    )
  }

  if (ctaStripVariant === 'homepage') {
    return (
      <HomepageCTABannerVariantModule
        module={module}
        trackingData={moduleTrackingData}
      />
    )
  }

  return <CTAStripModule trackingData={trackingData} {...module} />
}

export const renderPageModule = ({
  module,
  context,
  backgroundColor,
  page,
  trackingData,
  ctaStripVariant,
  deliveryStripVariant,
  productListOptions,
}: RenderPageModuleParams) => {
  const moduleTrackingData = {
    moduleIndex: trackingData.moduleIndex,
    moduleType: trackingData.moduleType,
  }

  switch (module.type) {
    case MODULE_CTA_STRIP:
      return (
        <ModuleSection key={context.index} data-testid="module-cta-strip">
          {renderCtaStripModule({
            module,
            trackingData,
            moduleTrackingData,
            ctaStripVariant,
          })}
        </ModuleSection>
      )

    case MODULE_RECOMMENDATIONS_FOR_CUSTOMER:
      return (
        <RecommendationsForCustomerModule
          key={context.index}
          module={module}
          context={context}
        />
      )

    case MODULE_PLACEHOLDER:
      return (
        <ModuleErrorBoundary
          key={context.index}
          type={module.placeholderVariant}
        >
          <PlaceholderModule
            {...module}
            backgroundColor={backgroundColor}
            trackingDataProps={{
              pageLocation: page,
              moduleIndex: trackingData.productCarouselIndexOnPage,
              modulesLength: trackingData.numberOfProductCarouselsOnPage,
            }}
          />
        </ModuleErrorBoundary>
      )

    case MODULE_DELIVERY_STRIP:
      return (
        <ModuleSection key={context.index} data-testid="module-delivery-strip">
          {deliveryStripVariant === 'homepage' ? (
            <HomepageBannerVariantModule {...module} />
          ) : (
            <DeliveryStripModule {...module} />
          )}
        </ModuleSection>
      )

    case MODULE_OFFER_STRIP:
      return (
        <ModuleSection key={context.index} data-testid="module-offer-strip">
          <OfferStripModule {...module} />
        </ModuleSection>
      )

    case MODULE_SEARCH_RESULTS:
      return (
        <ProductListModule
          {...module}
          {...productListOptions}
          trackingData={{
            pageLocation: page,
            moduleIndex: trackingData.productCarouselIndexOnPage,
            modulesLength: trackingData.numberOfProductCarouselsOnPage,
            moduleTitle: module.title,
          }}
          backgroundColor={backgroundColor}
        />
      )

    case MODULE_VIDEO:
      return (
        <ModuleSection
          {...context}
          withContainer
          data-testid="module-video"
          padding={LARGE_SPACING_PADDING}
        >
          <VideoModule {...module} />
        </ModuleSection>
      )

    case MODULE_TWO_COLUMN_PLACARD:
      return (
        <ModuleSection
          {...context}
          data-testid="module-two-column-placard"
          withContainer
          padding={LARGE_SPACING_PADDING}
        >
          <TwoColumnPlacardModule
            trackingData={moduleTrackingData}
            {...module}
          />
        </ModuleSection>
      )

    case MODULE_TABBED_PRODUCT_LISTS:
      return mapModule(
        module as unknown as Parameters<typeof mapModule>[0],
        context,
      )

    case MODULE_BREADCRUMBS:
    case MODULE_HEADING:
    case MODULE_HERO:
    case MODULE_MISSION_BUTTONS:
    case MODULE_TILES:
    case MODULE_INFO:
    case MODULE_SEO:
    case MODULE_PLACARDS:
    case MODULE_USP_CAROUSEL:
    case MODULE_CARD:
    case MODULE_CAROUSEL:
    case MODULE_EMAIL_CAPTURE:
      return mapModule(module as Parameters<typeof mapModule>[0], context)

    default:
      return null
  }
}
